/**
 * UN EXERCICE DU MANUEL, TEL QUE L'ÉLÈVE LE REÇOIT — et, à côté, ce que Silex
 * attend. Refonte de la console, lot L3 (retour du fondateur, 15 octobre :
 * « AUCUNE figure n'est affichée pour les exercices de maths ; AUCUN aperçu »).
 *
 * ─── Le rendu est celui du produit, pas un second ───────────────────────────
 *
 *  - les formules : `TexteMaths` → `texteVersHtml` (`@silex/shared`), le même
 *    analyseur que l'application ;
 *  - la figure, le diagramme, le solide, la table : `IllustrationWeb`,
 *    `DiagrammeWeb`, `SolideWeb`, `TableWeb` (`components/fiche/`) — la MÊME
 *    liste de traces que l'application peint en `react-native-svg`
 *    (`resoudreDessin`, `figureVersSvg`, `resoudreDiagramme`,
 *    `resoudreSolide`). Une figure que le moteur refuse se DIT, avec sa
 *    description : jamais un dessin à moitié.
 *
 * Un exercice à TRACER (chantier « Élève », lot 8, L0, AB#943) : au lieu de
 * « N objets attendus, M contraintes », la solution pas à pas, les contraintes
 * en phrases, les tracés fautifs rejoués, et un bac à sable (`TraceARelire`).
 *
 * Présentationnel : il reçoit la réponse de `GET /v1/tuteur/manuel/exercices/:code`
 * et ne lit rien d'autre — éprouvé seul (`exercice-a-relire.test.tsx`).
 */
import type { ExerciceRelectureResponse, SolideDecrit } from '@silex/shared';
import { useMemo, type ReactNode } from 'react';

import { bonneReponse, NOM_NIVEAU_EXERCICE } from '../../lib/relecture-exercices';
import { traceRelu } from '../../lib/relecture-trace';
import { DiagrammeWeb, IllustrationWeb, SolideWeb } from '../fiche/DessinWeb';
import { TableWeb } from '../fiche/TableWeb';
import { TexteMaths } from '../TexteMaths';
import { AttenduDuTrace, BacASableDuTrace } from './TraceARelire';

/** Une option de choix peut être un solide (`CAP-11`) ; une option de vrai ou faux, non. */
const solideDeLOption = (o: { readonly id: string }): SolideDecrit | undefined =>
  (o as { readonly solide?: SolideDecrit }).solide;

/** Un texte riche, ses retours à la ligne gardés. */
function Riche({ texte }: { readonly texte: string }): ReactNode {
  return (
    <>
      {texte.split('\n').map((ligne, i) => (
        <span key={i} className="rx-ligne">
          <TexteMaths texte={ligne} />
        </span>
      ))}
    </>
  );
}

function CeQueLEleveRecoit({ d }: { readonly d: ExerciceRelectureResponse }): ReactNode {
  const e = d.exercice;
  const attendue = bonneReponse(d.corrige);
  const options =
    e.saisie.nature === 'choix' || e.saisie.nature === 'vrai_faux' ? e.saisie.options : null;
  const fautive = (id: string) => d.reponsesFautives.find((r) => r.reponse === id);
  /* Un exercice à tracer (AB#943, L0) : le relecteur peut tracer comme l'élève. */
  const trace = useMemo(() => traceRelu(d), [d]);
  return (
    <div className="rx-eleve" data-testid="rx-eleve">
      <div className="rx-kicker">Ce que l’élève reçoit</div>
      {e.titre ? (
        <h2 className="rx-titre">
          <TexteMaths texte={e.titre} />
        </h2>
      ) : null}

      {(e.supports ?? []).map((s) => (
        <section key={s.code} className="rx-support" aria-label={`Texte : ${s.titre}`}>
          <h3>{s.titre}</h3>
          {s.reference ? <div className="cell-sub">{s.reference}</div> : null}
          <ol className="rx-support__lignes">
            {s.paragraphes.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ol>
        </section>
      ))}

      <div className="rx-enonce" data-testid="rx-enonce">
        <Riche texte={e.enonce} />
      </div>

      {e.figure ? (
        <div className="rx-figure" data-testid="rx-figure">
          <IllustrationWeb illustration={e.figure} largeurMax={440} decritVisible />
        </div>
      ) : null}
      {e.table ? (
        <div className="rx-figure" data-testid="relecture-table">
          <TableWeb table={e.table} />
        </div>
      ) : null}
      {e.diagramme ? (
        <div className="rx-figure" data-testid="relecture-diagramme">
          <DiagrammeWeb
            diagramme={e.diagramme.diagramme}
            decrit={e.diagramme.decrit}
            decritVisible
            largeurMax={440}
          />
        </div>
      ) : null}
      {e.solide ? (
        <div className="rx-figure" data-testid="relecture-solide">
          <SolideWeb solide={e.solide} decritVisible largeurMax={440} />
        </div>
      ) : null}

      {options ? (
        <ul className="rx-options" data-testid="rx-options">
          {options.map((o) => {
            const bonne = attendue.genre === 'option' && attendue.id === o.id;
            const f = fautive(o.id);
            return (
              <li key={o.id} className={bonne ? 'rx-option rx-option--bonne' : 'rx-option'}>
                <b className="contenus-mono">{o.id}</b>
                <span className="rx-option__texte">
                  <TexteMaths texte={o.texte} />
                  {solideDeLOption(o) ? (
                    <SolideWeb solide={solideDeLOption(o)!} largeurMax={160} />
                  ) : null}
                </span>
                {bonne ? <span className="badge badge--acquis">Bonne réponse</span> : null}
                {!bonne && f ? (
                  <span className="rx-fautive" title={f.cheminement}>
                    → {f.erreur?.titre ?? 'réponse fautive sans erreur nommée'}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {e.saisie.nature === 'table' ? (
        <div className="rx-figure">
          <div className="cell-sub">Table à remplir</div>
          <TableWeb table={e.saisie.table} testid="relecture-table-a-remplir" />
        </div>
      ) : null}
      {e.saisie.nature === 'matrice' ? (
        <div className="rx-figure">
          <div className="cell-sub">Matrice à remplir</div>
          <TableWeb table={e.saisie.table} testid="relecture-matrice-a-remplir" />
        </div>
      ) : null}
      {e.saisie.nature === 'diagramme' ? (
        <div className="rx-figure">
          <div className="cell-sub">Diagramme à compléter</div>
          <DiagrammeWeb diagramme={e.saisie.diagramme} largeurMax={440} />
        </div>
      ) : null}

      <p className="note-line">
        Niveau {e.niveauExercice} · {NOM_NIVEAU_EXERCICE[e.niveauExercice]} · environ{' '}
        {e.minutesEstimees} min · réponse : {e.saisie.nature.replace('_', ' ')}
        {e.demarche !== 'aucune' ? ` · démarche ${e.demarche}` : ''}
      </p>

      {trace !== null ? <BacASableDuTrace key={e.code} t={trace} /> : null}

      <h3 className="rx-h3">Indices, dans l’ordre</h3>
      {d.indices.length > 0 ? (
        <ol className="rx-liste" data-testid="relecture-indices">
          {d.indices.map((i, k) => (
            <li key={k}>
              <Riche texte={i} />
            </li>
          ))}
        </ol>
      ) : (
        <p className="note-line" data-testid="relecture-indices">
          {e.nombreIndices > 0
            ? 'Aucun indice écrit dans la source : ils sont produits à la demande, pendant la séance.'
            : 'Aucun indice pour cet exercice.'}
        </p>
      )}

      {e.criteres ? (
        <>
          <h3 className="rx-h3">Ce qui est regardé</h3>
          <ul className="rx-liste">
            {e.criteres.map((c) => (
              <li key={c.critere}>
                <b>{c.libelle}</b> — <Riche texte={c.attendu} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function CeQueSilexAttend({ d }: { readonly d: ExerciceRelectureResponse }): ReactNode {
  const attendue = bonneReponse(d.corrige);
  /* Un exercice à tracer (AB#943, L0) : la solution, les contraintes en phrases, les tracés fautifs rejoués. */
  const trace = useMemo(() => traceRelu(d), [d]);
  const options =
    d.exercice.saisie.nature === 'choix' || d.exercice.saisie.nature === 'vrai_faux'
      ? d.exercice.saisie.options
      : null;
  return (
    <div className="rx-prof" data-testid="rx-prof">
      <div className="rx-kicker">Ce que Silex attend</div>
      <div className="rx-bonne" data-testid="rx-bonne-reponse">
        <b>Bonne réponse.</b>{' '}
        {attendue.genre === 'option' ? (
          <>
            {attendue.id}
            {options ? (
              <>
                {' — '}
                <TexteMaths texte={options.find((o) => o.id === attendue.id)?.texte ?? ''} />
              </>
            ) : null}
          </>
        ) : null}
        {attendue.genre === 'texte' ? <Riche texte={attendue.texte} /> : null}
        {attendue.genre === 'trace'
          ? trace !== null
            ? 'le tracé ci-dessous.'
            : attendue.texte
          : null}
        {attendue.genre === 'texte' && attendue.details
          ? attendue.details.map((t) => (
              <div key={t} className="cell-sub">
                <TexteMaths texte={t} />
              </div>
            ))
          : null}
        {attendue.genre === 'table' ? (
          <>
            {attendue.cases} case{attendue.cases > 1 ? 's' : ''} à remplir.
            <TableWeb table={attendue.table} testid="relecture-table-corrigee" />
          </>
        ) : null}
        {attendue.genre === 'diagramme' ? (
          <DiagrammeWeb diagramme={attendue.corrige.diagramme} largeurMax={380} />
        ) : null}
      </div>

      {trace !== null ? <AttenduDuTrace key={d.exercice.code} t={trace} /> : null}

      {d.demarcheReference.length > 0 ? (
        <>
          <h3 className="rx-h3">Démarche de référence</h3>
          <ol className="rx-liste" data-testid="relecture-demarche">
            {d.demarcheReference.map((s) => (
              <li key={s.rang}>
                <Riche texte={s.ecriture} />
                <div className="cell-sub">
                  <Riche texte={s.justification} />
                </div>
              </li>
            ))}
          </ol>
        </>
      ) : null}

      {d.reponsesFautives.length > 0 && !options ? (
        <>
          <h3 className="rx-h3">Réponses fautives prévues</h3>
          <ul className="rx-liste rx-liste--plate">
            {d.reponsesFautives.map((r, k) => (
              <li key={k}>
                <span className="contenus-mono">
                  <TexteMaths texte={r.reponse} />
                </span>{' '}
                → <b>{r.erreur?.titre ?? 'sans erreur nommée'}</b>
                {r.cheminement ? (
                  <div className="cell-sub">
                    <Riche texte={r.cheminement} />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <p className="note-line" data-testid="relecture-notions">
        Mesure :{' '}
        {d.notions
          .map(
            (n) =>
              `${n.rang === null ? 'prérequis' : `notion ${n.rang}`} « ${n.titre} » (${Math.round(
                n.poids * 100,
              )} %)`,
          )
          .join(' · ')}
      </p>
    </div>
  );
}

export function ExerciceARelire({ detail }: { readonly detail: ExerciceRelectureResponse }) {
  return (
    <div className="rx-detail__colonnes">
      <CeQueLEleveRecoit d={detail} />
      <CeQueSilexAttend d={detail} />
    </div>
  );
}
